"use client";

import { MODAL_WIDTH } from "@/constants";
import { type PhoneOtpError, usePhoneOtp } from "@/hooks";
import { analytics } from "@analytics";
import {
  Body,
  Box,
  ButtCon,
  Button,
  FormControl,
  Heading,
  Link,
  Modal,
  PasscodeInput,
  Radio,
  Stack,
} from "@biom3/react";
import TextsmsIcon from "@mui/icons-material/Textsms";
import { useTimeCountdown } from "@ui-kit/hooks";
import IntlTelInput from "intl-tel-input/react";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { RewardsChip } from "../RewardsChip";

const errorMap = [
  "Invalid number",
  "Invalid country code",
  "Too short",
  "Too long",
  "Invalid number",
];

enum VerificationChannel {
  SMS = "sms",
  WHATSAPP = "whatsapp",
}

const AllowSMS = new Set([
  "ar",
  "at",
  "au",
  "bh",
  "bn",
  "bs",
  "ca",
  "ch",
  "cn",
  "co",
  "cr",
  "cu",
  "cy",
  "cz",
  "dk",
  "ee",
  "es",
  "fi",
  "fm",
  "fo",
  "fr",
  "gl",
  "gr",
  "gu",
  "hk",
  "hu",
  "ie",
  "is",
  "it",
  "jp",
  "kr",
  "li",
  "lt",
  "lu",
  "lv",
  "mk",
  "mo",
  "mt",
  "nf",
  "no",
  "pl",
  "pr",
  "pt",
  "pw",
  "ro",
  "sg",
  "sk",
  "th",
  "tr",
  "tw",
  "us",
  "uy",
]);

const OTP_CODE_LENGTH = 6;

interface PhoneVerificationModalProps {
  visible: boolean;
  onVerified: () => void;
  onClose: () => void;
}

function StatusImage({ type }: { type: "success" | "error" }) {
  return (
    <Box
      key={type}
      sx={{
        position: "absolute",
        bottom: "0",
        right: "base.spacing.x20",
      }}
    >
      <Image
        src={`/images/profile/status-${type}.svg`}
        alt="Phone verification"
        width={92}
        height={109}
        priority={true}
      />
    </Box>
  );
}

function usePreloadStatusImages() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const statusImages = [
      "/images/profile/status-success.svg",
      "/images/profile/status-error.svg",
    ];

    for (const src of statusImages) {
      const img = new window.Image();
      img.src = src;
    }
  }, []);
}

export const PhoneVerificationModal = ({
  visible,
  onVerified,
  onClose,
}: PhoneVerificationModalProps) => {
  usePreloadStatusImages();

  const [phoneNumber, setPhoneNumber] = useState("");
  const phoneNumberRef = useRef("");
  const [modalState, setModalState] = useState<
    "phone" | "verify" | "success" | "error"
  >("phone");
  const verificationCodeExpiryRef = useRef<Date | undefined>(undefined);
  const { formatted: verificationCountdown } = useTimeCountdown(
    verificationCodeExpiryRef.current,
  );

  const [verifyCodeInput, setVerifyCodeInput] = useState("");

  const [hasResentCode, setHasResentCode] = useState(false);
  const [phoneValidationError, setPhoneValidationError] = useState("");
  const [apiError, setApiError] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const isPhoneValidRef = useRef(false);
  const [resendCooldownEnd, setResendCooldownEnd] = useState<Date | undefined>(
    undefined,
  );
  const resendCountdown = useTimeCountdown(resendCooldownEnd);
  const [isClient, setIsClient] = useState(false);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const errorCodeRef = useRef<number | null>(null);

  const [isSendingCode, setIsSendingCode] = useState(false);

  const { sendOtp, verifyOtp } = usePhoneOtp();

  const [selectedVerificationChannel, setSelectedMethod] =
    useState<VerificationChannel>(VerificationChannel.WHATSAPP);

  const [selectedCountry, setSelectedCountry] = useState("us");

  const handleCountryChange = (countryData: string) => {
    if (countryData === selectedCountry) return;

    setSelectedCountry(countryData);
    setSelectedMethod(VerificationChannel.WHATSAPP);
  };

  useEffect(() => {
    setIsClient(true);
    // Dynamically import IntlTelInput styles only when this component is used
    // @ts-expect-error - CSS module import doesn't have type declarations
    import("intl-tel-input/styles");
  }, []);

  useEffect(() => {
    if (!visible) {
      setPhoneNumber("");
      setModalState("phone");
      setPhoneValidationError("");
      setApiError("");
      setIsPhoneValid(false);
      setErrorCode(null);
    }
  }, [visible]);

  useEffect(() => {
    phoneNumberRef.current = phoneNumber;
  }, [phoneNumber]);

  useEffect(() => {
    isPhoneValidRef.current = isPhoneValid;
  }, [isPhoneValid]);

  useEffect(() => {
    errorCodeRef.current = errorCode;
  }, [errorCode]);

  const handleApiError = useCallback((error: PhoneOtpError) => {
    const errorMessage = error.message.toLowerCase();
    if (error.status === 429) {
      setApiError(
        "Verification attempts limit reached. Please try again in 24 hours.",
      );
    } else if (
      errorMessage.includes("user already has verified phone number")
    ) {
      setApiError("You already have a verified phone number.");
    } else if (errorMessage.includes("invalid phone number")) {
      setApiError("Invalid phone number. Please check and try again.");
    } else if (errorMessage.includes("request body has an error")) {
      setApiError("Invalid format. Please check and try again.");
    } else if (error.status === 400) {
      // If neither the above and still 400, we give a gentle error message.
      switch (error.code) {
        case "expired_code":
          setApiError("Expired code. Please resend another code.");
          break;
        case "invalid_verification_code":
          setApiError("Invalid code. Please try again.");
          break;
        case "sanctioned_blocked":
          setApiError(
            "We cannot verify phone numbers from your region in compliance with regulations. Some of our services may not be available in your region.",
          );
          break;
        case "already_linked":
          setApiError(
            "This phone number is already linked to another account. Please contact support if you believe this may be an error.",
          );
          break;
        case "fraudulent_activity":
          setApiError(
            "Our verification system flagged this number and cannot be used. Please contact support if you believe this may be an error.",
          );
          break;
        default:
          setApiError("Invalid or expired code. Please try again.");
      }
    } else {
      setModalState("error");
    }
  }, []);

  const handleSendVerificationCode = useCallback(async () => {
    analytics.track("Play_Button_Pressed", {
      controlLabel: "Send Verification Code",
      userJourney: "Rewards",
      section: "PhoneVerificationModal",
    });

    setIsSendingCode(true);
    // Use the ref values instead of the state values
    const currentPhoneNumber = phoneNumberRef.current;
    const currentIsPhoneValid = isPhoneValidRef.current;
    const currentErrorCode = errorCodeRef.current;

    if (!currentIsPhoneValid) {
      const errorMessage =
        currentErrorCode !== null
          ? errorMap[currentErrorCode] || "Invalid phone number"
          : "Please enter a valid phone number";
      setPhoneValidationError(errorMessage);
      setIsSendingCode(false);
      return;
    }

    // Clear validation errors since the phone is valid
    setPhoneValidationError("");

    const result = await sendOtp(
      currentPhoneNumber,
      selectedVerificationChannel,
    );
    setIsSendingCode(false);

    if (result?.error) {
      handleApiError(result.error);
      return;
    }

    setPhoneNumber(result.phone_number);
    verificationCodeExpiryRef.current = new Date(Date.now() + 15 * 60 * 1000);

    setApiError("");
    setModalState("verify");
  }, [sendOtp, handleApiError, selectedVerificationChannel]);

  const handleVerifyCode = useCallback(
    async (verificationCode: string) => {
      analytics.track("Play_Button_Pressed", {
        controlLabel: "Verify Code",
        userJourney: "Rewards",
        section: "PhoneVerificationModal",
      });

      const currentPhoneNumber = phoneNumberRef.current;
      const result = await verifyOtp(currentPhoneNumber, verificationCode);

      if (!result?.success) {
        if (result?.error) {
          handleApiError(result.error);
        } else {
          setApiError("Failed to verify code");
        }
        return;
      }

      // Clear any errors on success
      setApiError("");
      setModalState("success");

      const timeout = setTimeout(() => {
        onVerified();
      }, 3000);

      return () => clearTimeout(timeout);
    },
    [verifyOtp, handleApiError, onVerified],
  );

  const handleResendCode = async () => {
    // Prevent resend if cooldown is active
    if (resendCooldownEnd && new Date() < resendCooldownEnd) {
      return;
    }

    await handleSendVerificationCode();
    setHasResentCode(true);

    // Set cooldown for 60 seconds
    setResendCooldownEnd(new Date(Date.now() + 60 * 1000));

    // Reset hasResentCode after 5 seconds
    setTimeout(() => {
      setHasResentCode(false);
    }, 5000);
  };

  const handleValidityChange = (isValid: boolean) => {
    setIsPhoneValid(isValid);
    isPhoneValidRef.current = isValid;

    if (isValid && phoneValidationError) {
      setPhoneValidationError("");
    }
  };

  const onPasscodeChange: (value: string) => void = useCallback(
    (verificationCode) => {
      const trimmed = verificationCode.trim();
      setVerifyCodeInput(trimmed);
      if (trimmed.length === OTP_CODE_LENGTH) {
        handleVerifyCode(trimmed);
      }
    },
    [handleVerifyCode],
  );

  const VerificationCodeInput = () => {
    const isResendDisabled =
      resendCooldownEnd && new Date() < resendCooldownEnd;

    return (
      <Stack sx={{ alignItems: "center" }}>
        <Stack
          direction="row"
          alignItems="center"
          gap="base.spacing.x2"
          sx={{ mt: "base.spacing.x2" }}
        >
          {!isResendDisabled && (
            <Link onClick={handleResendCode} disabled={isResendDisabled}>
              Resend code
            </Link>
          )}
          {isResendDisabled && resendCountdown && (
            <Body size="xSmall" sx={{ color: "base.color.text.body.tertiary" }}>
              You can resend again in {resendCountdown.formatted}
            </Body>
          )}
          {isSendingCode && (
            <Button.Icon icon="Loading" variant="primary/inverse" />
          )}
          {hasResentCode && !isSendingCode && (
            <Button.Icon
              icon="TickWithCircle"
              variant="primary/inverse"
              sx={{ fill: "base.color.text.status.success.primary" }}
            />
          )}
        </Stack>
        <Stack
          id="actions"
          alignItems="center"
          gap="base.spacing.x4"
          sx={{ mt: "base.spacing.x8" }}
        >
          <PasscodeInput
            sx={{ gap: "base.spacing.x2" }}
            validationStatus={
              apiError && verifyCodeInput.length > 0 ? "error" : undefined
            }
            testId="passwordless_passcode"
            id="passwordless_container"
            passcodeLength={OTP_CODE_LENGTH}
            onPasscodeChange={onPasscodeChange}
          />
          {apiError ? (
            <FormControl.Validation validationStatus="error">
              {apiError}
            </FormControl.Validation>
          ) : verificationCodeExpiryRef.current !== undefined ? (
            <FormControl.Caption>
              {verificationCountdown ? (
                <>Code will expire in {verificationCountdown}</>
              ) : (
                <>Code expired.</>
              )}
            </FormControl.Caption>
          ) : null}
        </Stack>
      </Stack>
    );
  };

  const PhoneInput = () => {
    return (
      <Stack
        sx={{ my: "base.spacing.x8", width: "100%" }}
        gap="base.spacing.x4"
      >
        <Stack direction="column" gap="base.spacing.x1">
          <Body size="small">Phone number</Body>
          {isClient && (
            <IntlTelInput
              initOptions={{
                excludeCountries: ["cn"],
                initialCountry: "us",
                separateDialCode: true,
                autoPlaceholder: "aggressive",
                nationalMode: false,
                formatOnDisplay: true,
                ...(isClient && { dropdownContainer: document.body }),
                loadUtils: () => {
                  // @ts-ignore - Importing from npm package
                  return import("intl-tel-input/build/js/utils.js");
                },
              }}
              onChangeNumber={(value) => {
                setPhoneNumber(value);
              }}
              onChangeValidity={handleValidityChange}
              onChangeErrorCode={(code) => {
                setErrorCode(code);
                errorCodeRef.current = code;
              }}
              onChangeCountry={handleCountryChange}
              inputProps={{
                placeholder: "Enter your phone number",
                className: phoneValidationError ? "is-invalid" : "",
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (
                    e.key === "Enter" &&
                    phoneNumber.length > 0 &&
                    !isSendingCode
                  ) {
                    e.preventDefault();
                    handleSendVerificationCode();
                  }
                },
              }}
            />
          )}
          {!phoneValidationError && !apiError && (
            <Body size="small" sx={{ color: "base.color.text.body.secondary" }}>
              We'll send a verification code to this number
            </Body>
          )}
          {apiError ? (
            <FormControl.Validation validationStatus="error">
              {apiError}
            </FormControl.Validation>
          ) : phoneValidationError ? (
            <FormControl.Validation validationStatus="error">
              {phoneValidationError}
            </FormControl.Validation>
          ) : null}
        </Stack>

        {
          <Stack direction="column" gap="base.spacing.x1">
            <Body size="small">Receive your code via</Body>
            <Stack direction="column" gap="base.spacing.x2">
              <Stack direction="row" alignItems="center" gap="base.spacing.x2">
                <Radio
                  checked={
                    selectedVerificationChannel === VerificationChannel.WHATSAPP
                  }
                  onChange={() =>
                    setSelectedMethod(VerificationChannel.WHATSAPP)
                  }
                  value={VerificationChannel.WHATSAPP}
                />
                <img
                  src="/icons/whatsapp-icon.svg"
                  alt="WhatsApp"
                  style={{ opacity: 0.8, width: 24, height: 24 }}
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    minWidth: "70px",
                    justifyContent: "flex-end",
                  }}
                >
                  <Body
                    size="small"
                    sx={{ color: "base.color.text.body.secondary" }}
                  >
                    Whatsapp
                  </Body>
                  <RewardsChip amount={100} completed={true} />
                </Stack>
              </Stack>
              {AllowSMS.has(selectedCountry) && (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap="base.spacing.x2"
                >
                  <Radio
                    checked={
                      selectedVerificationChannel === VerificationChannel.SMS
                    }
                    onChange={() => setSelectedMethod(VerificationChannel.SMS)}
                    value={VerificationChannel.SMS}
                  />
                  <TextsmsIcon sx={{ opacity: 0.8 }} />
                  <Body
                    size="small"
                    sx={{ color: "base.color.text.body.secondary" }}
                  >
                    SMS
                  </Body>
                </Stack>
              )}
            </Stack>
          </Stack>
        }
      </Stack>
    );
  };

  return (
    <>
      <Modal
        visible={visible}
        onCloseModal={onClose}
        outsideClicksClose={false}
      >
        <Modal.Content
          sx={{
            height: "auto",
            maxHeight: "90vh",
            overflowY: "auto",
            width: MODAL_WIDTH,
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "base.color.translucent.inverse.700",
            backdropFilter: "blur(32px)",
            borderRadius: "base.borderRadius.x10",
            border: "1px solid",
            borderColor: "base.color.translucent.standard.500",
            position: "relative",
            padding: "base.spacing.x12",
          }}
        >
          <ButtCon
            icon="Close"
            variant="tertiary"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: "base.spacing.x4",
              top: "base.spacing.x4",
            }}
          />
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Image
              src="/images/profile/phone-illustration.webp"
              alt="Phone verification"
              width={81}
              height={213}
              priority={true}
              style={{
                width: "auto",
                height: "auto",
              }}
            />
            <AnimatePresence>
              <motion.div
                key={modalState}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {modalState === "success" && <StatusImage type={"success"} />}
                {modalState === "error" && <StatusImage type={"error"} />}
              </motion.div>
            </AnimatePresence>
          </Box>
          <Heading
            size="small"
            sx={{
              mt: "base.spacing.x10",
              textAlign: "center",
            }}
          >
            {modalState === "phone" && <span>Verify phone number</span>}
            {modalState === "verify" && <span>Confirm phone number</span>}
            {modalState === "success" && <span>Phone number verified</span>}
            {modalState === "error" && (
              <span>Unable to complete verification</span>
            )}
          </Heading>

          <Body
            size="small"
            sx={{
              mt: "base.spacing.x2",
              color: "base.color.text.body.secondary",
              textAlign: "center",
            }}
          >
            {modalState === "phone" && (
              <span>Confirm your phone number for added security.</span>
            )}
            {modalState === "verify" && (
              <span>Enter the code we sent to {phoneNumber}</span>
            )}
            {modalState === "success" && <span>You're all set</span>}
            {modalState === "error" && (
              <span>
                Something went wrong and we're unable to complete this
                verification.
              </span>
            )}
          </Body>

          {modalState === "phone" && PhoneInput()}
          {modalState === "verify" && VerificationCodeInput()}

          {modalState === "phone" && (
            <Button
              variant="primary"
              onClick={handleSendVerificationCode}
              disabled={phoneNumber.length === 0 || isSendingCode}
              sx={{ width: "100%", mt: "auto" }}
            >
              Send{isSendingCode && "ing"} code
              {isSendingCode && <Button.Icon icon="Loading" />}
            </Button>
          )}
          {modalState === "error" && (
            <Button
              variant="primary"
              onClick={onClose}
              sx={{ width: "100%", mt: "auto" }}
            >
              Done
            </Button>
          )}
        </Modal.Content>
      </Modal>
    </>
  );
};
